import React, { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { createClient, fetchExchange, Provider as UrqlProvider } from "urql";
import { authExchange } from "@urql/exchange-auth";
import { Auth } from "aws-amplify";
import { AppSyncConfig } from "constants/amplify";
import { useAuth } from "hooks/useAuth";

const AuthedUrqlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authStatus } = useAuth();

  /**
   * Cognito から AppSync GraphQL 用の JWT ログイントークンを取得する。
   */
  const getAuthToken = async () => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      return null;
    }
  }

  const clientAuthExchange = authExchange(async (utils) => {
    let sessionToken: string | null = null;
    sessionToken = await getAuthToken();

    return {
      addAuthToOperation(operation) {
        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${sessionToken}`,
        });
      },
      didAuthError(error) {
        return error.response.status === 401;
      },
      async refreshAuth() {
        sessionToken = await getAuthToken();
      },
    }
  })

  const client = useMemo(() => {
    if (authStatus !== "authenticated") return null;

    return createClient({
      url: AppSyncConfig.graphql_endpoint,
      exchanges: [
        // cacheExchange,
        clientAuthExchange,
        fetchExchange,
      ],
    });
  }, [authStatus]);

  if (authStatus === "initializing") {
    return <></>;
  }

  if (authStatus === "unauthenticated") {
    return <Navigate replace to="/auth/signIn"/>;
  }

  if (!client) {
    return <></>;
  }

  return <UrqlProvider value={client}>{children}</UrqlProvider>;
}

export default AuthedUrqlProvider;
